import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMyGroups } from '@/features/groups/hooks/useGroups';

/**
 * A dónde lleva «Nuevo gasto», que no es una ruta sino cuatro destinos.
 *
 * Vivía dentro del botón flotante, y ahí se quedó mientras fue el único sitio
 * desde donde se creaba un gasto. Desde que la barra lateral de escritorio tiene
 * su propio botón son **dos**, y dos copias de esta decisión divergen el día que
 * se toque una: bastaría con arreglar un caso en el flotante y olvidarse del
 * otro para que la aplicación se comportara distinto según el ancho de pantalla.
 *
 * Los cuatro destinos, en orden:
 *
 *   1. Dentro de un grupo → el formulario de ese grupo, sin preguntar.
 *   2. Sin ningún grupo → crear el primero: no hay dónde meter un gasto.
 *   3. Un solo grupo → su formulario, sin preguntar tampoco. Un selector de un
 *      elemento es un clic que no decide nada.
 *   4. Varios → el selector.
 */
export function useNuevoGasto() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: grupos = [], isLoading } = useMyGroups();
  const [selectorAbierto, setSelectorAbierto] = useState(false);

  const ruta = location.pathname;

  // Dónde no tiene sentido ofrecerlo: en las pantallas de acceso no hay sesión,
  // y estando ya en el formulario de un gasto el botón se ofrecería a llevarte
  // donde ya estás.
  const enAcceso = ['/login', '/register', '/auth/callback'].some((p) => ruta.startsWith(p));
  const enFormularioGasto = ruta.endsWith('/expenses/new') || /\/expenses\/[^/]+\/edit$/.test(ruta);
  const enFormularioGrupo = ruta === '/groups/new';
  const oculto = enAcceso || enFormularioGasto || enFormularioGrupo;

  const coincidencia = ruta.match(/^\/groups\/([^/]+)(?:\/|$)/);
  const grupoActual = coincidencia && coincidencia[1] !== 'new' ? coincidencia[1] : null;

  const activos = grupos.filter((g) => !g.is_archived);

  /**
   * `false` mientras la consulta está en vuelo, y por eso se distingue de «no
   * hay»: pintar el botón apagado durante la carga y encenderlo medio segundo
   * después es peor que dejarlo encendido, porque el usuario ya ha decidido que
   * no se puede pulsar y ha apartado la vista.
   */
  const hayGrupos = activos.length > 0;

  const ir = () => {
    if (grupoActual) {
      navigate(`/groups/${grupoActual}/expenses/new`);
      return;
    }
    if (activos.length === 0) {
      navigate('/groups/new');
      return;
    }
    if (activos.length === 1) {
      const unico = activos[0];
      if (unico) navigate(`/groups/${unico.id}/expenses/new`);
      return;
    }
    setSelectorAbierto(true);
  };

  return {
    oculto,
    hayGrupos,
    cargando: isLoading,
    /** Con un grupo abierto el gasto va ahí, y el botón no depende de tener más. */
    dentroDeGrupo: grupoActual !== null,
    ir,
    selectorAbierto,
    cerrarSelector: () => setSelectorAbierto(false),
  };
}
